import React, { useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import {
   acceptTransferApi,
   rejectTransferApi,
   type ApiDeviceTransfer,
} from '@/api/vendo';

interface Props {
   data: { transfer: ApiDeviceTransfer };
}

const ReviewTransfer = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const queryClient = useQueryClient();
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const { transfer } = data;

   const [working, setWorking] = useState<'accept' | 'reject' | null>(null);

   const seller =
      typeof transfer.fromOwner === 'object'
         ? transfer.fromOwner.name || transfer.fromOwner.email
         : transfer.fromOwner;
   const sellerEmail =
      typeof transfer.fromOwner === 'object'
         ? transfer.fromOwner.email
         : transfer.fromOwner;
   const dev = typeof transfer.device === 'object' ? transfer.device : null;

   const handleAccept = async () => {
      if (working) return;
      setWorking('accept');
      try {
         await acceptTransferApi(transfer._id);
         toast.success('Device accepted — it is now in your dashboard');
         queryClient.invalidateQueries({ queryKey: ['transfers'] });
         refetchDevices();
         handleClose();
      } catch (err: any) {
         const code = err?.response?.data?.code;
         const msg =
            err?.response?.data?.error ||
            (code === 'wrong-recipient'
               ? 'This transfer was sent to a different email.'
               : 'Could not accept the transfer');
         toast.error(msg);
         setWorking(null);
      }
   };

   const handleReject = async () => {
      if (working) return;
      setWorking('reject');
      try {
         await rejectTransferApi(transfer._id);
         toast.success('Transfer rejected');
         queryClient.invalidateQueries({ queryKey: ['transfers'] });
         handleClose();
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Could not reject');
         setWorking(null);
      }
   };

   return (
      <Wrapper>
         <Header>
            <DeviceName>{dev?.name ?? 'Unknown device'}</DeviceName>
            <PaymentBadge $type={transfer.paymentType}>
               {transfer.paymentType === 'full-cash'
                  ? `Paid · ₱${(transfer.fullCashAmount ?? 0).toLocaleString()}`
                  : transfer.paymentType === 'installment'
                  ? 'Installment'
                  : 'Plain Transfer'}
            </PaymentBadge>
         </Header>

         <MetaGrid>
            <MetaLabel>From</MetaLabel>
            <MetaValue>
               <strong>{seller}</strong>
               {seller !== sellerEmail && <Faint> · {sellerEmail}</Faint>}
            </MetaValue>

            {dev?.deviceId && (
               <>
                  <MetaLabel>Device ID</MetaLabel>
                  <MetaValue>
                     <Mono>{dev.deviceId}</Mono>
                  </MetaValue>
               </>
            )}

            <MetaLabel>Expires</MetaLabel>
            <MetaValue>
               {new Date(transfer.expiresAt).toLocaleDateString()}
            </MetaValue>
         </MetaGrid>

         {transfer.note && (
            <Note>
               <NoteLabel>Note from seller</NoteLabel>
               <NoteBody>"{transfer.note}"</NoteBody>
            </Note>
         )}

         <Notice>
            Accepting moves this device to your dashboard. Money is exchanged
            offline — SmartFi only records the transfer for the audit log.
         </Notice>

         <BtnRow>
            <div onClick={handleReject}>
               <Btn
                  title={working === 'reject' ? 'Rejecting…' : 'Reject'}
                  type={working ? 'disabled' : 'outline'}
                  size="small"
                  bgcolor={colors.WHITE}
                  textcolor={colors.DANGER}
               />
            </div>
            <div onClick={handleAccept}>
               <Btn
                  title={working === 'accept' ? 'Accepting…' : 'Accept transfer'}
                  type={working ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={working ? colors.GRAY10 : colors.PRIMARY}
                  textcolor={working ? colors.GRAY1 : colors.WHITE}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

export default ReviewTransfer;

const Wrapper = styled.div`
   width: 480px;
   max-width: 100%;
   padding: 4px 0 0;
`;

const Header = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   margin-bottom: 16px;
`;

const DeviceName = styled.div`
   font-size: 16px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const PaymentBadge = styled.span<{ $type: 'free' | 'full-cash' | 'installment' }>`
   padding: 4px 12px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   background: ${({ $type }) =>
      $type === 'full-cash'
         ? `${colors.SUCCESS}1f`
         : $type === 'installment'
         ? `${colors.WARNING}26`
         : `${colors.PRIMARY}15`};
   color: ${({ $type }) =>
      $type === 'full-cash'
         ? colors.SUCCESS
         : $type === 'installment'
         ? colors.WARNING
         : colors.PRIMARY};
`;

const MetaGrid = styled.div`
   display: grid;
   grid-template-columns: 90px 1fr;
   gap: 8px 14px;
   margin-bottom: 16px;
`;

const MetaLabel = styled.div`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   align-self: center;
`;

const MetaValue = styled.div`
   font-size: 12px;
   color: ${colors.GRAY_DARK};
   strong {
      font-weight: 700;
   }
`;

const Faint = styled.span`
   color: ${colors.GRAY5};
`;

const Mono = styled.span`
   font-family: 'SF Mono', Menlo, monospace;
   font-size: 11px;
`;

const Note = styled.div`
   background: ${colors.GRAY14};
   border-radius: 8px;
   padding: 10px 12px;
   margin-bottom: 14px;
`;

const NoteLabel = styled.div`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin-bottom: 4px;
`;

const NoteBody = styled.div`
   font-size: 12px;
   color: ${colors.GRAY_DARK};
   font-style: italic;
`;

const Notice = styled.div`
   background: ${colors.PRIMARY}10;
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.5;
   margin-bottom: 16px;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;
